import { useCallback } from "react";
import { Box, Typography, ButtonBase } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LinearProgressBar from "../../../Components/LinearProgressBar";
import { useSnackbar } from "../../../Components/SnackBar";
import { resolveVars, useWeeklyTasks } from "../hooks/useGrowCommunity";
import { MOCK_JOIN_LINK } from "../mockData";
import { copyText } from "../utils";
import { GROW_COMMUNITY_PATH, REPLY_HELPER_ID, SHARE_GRID_ID, revealSection } from "../sections";
import SectionCard from "./SectionCard";
import { g, CARD_BORDER, GREEN, GREEN_TINT, HOVER_BG, TEXT_MAIN, TEXT_MUTED, TEXT_SUB } from "../styles";

const SCROLL_TARGETS = { scrollToShare: SHARE_GRID_ID, scrollToHelper: REPLY_HELPER_ID };

const s = {
  progressRow: { gap: "10px" },
  progressBar: { flex: 1, minWidth: 0 },
  progressCount: { fontSize: "13px", fontWeight: 400, color: TEXT_SUB, whiteSpace: "nowrap" },
  allDone: { fontSize: "13px", fontWeight: 500, color: GREEN, backgroundColor: GREEN_TINT, borderRadius: "8px", padding: "6px 12px", marginBottom: "8px" },
  list: { display: "flex", flexDirection: "column", gap: "8px" },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "10px 12px",
    borderRadius: "10px",
    border: CARD_BORDER,
    textAlign: "start",
    justifyContent: "flex-start",
    "&:hover": { backgroundColor: HOVER_BG },
    "&.Mui-focusVisible": { outline: `2px solid ${GREEN}`, outlineOffset: "1px" },
  },
  rowText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" },
  label: (done) => ({
    fontSize: "14px",
    fontWeight: 500,
    color: done ? TEXT_MUTED : TEXT_MAIN,
    lineHeight: 1.4,
    textDecoration: done ? "line-through" : "none",
  }),
  hint: { fontSize: "12px", color: TEXT_SUB, lineHeight: 1.4 },
  chevron: { color: "rgba(62, 63, 76, 0.4)", fontSize: 20 },
  // Compact variant (sidebar popup): plain rows, no borders or hints.
  compactRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    padding: "6px 0",
    textAlign: "start",
    justifyContent: "flex-start",
    borderRadius: "6px",
    "&.Mui-focusVisible": { outline: `2px solid ${GREEN}` },
  },
  openPage: {
    marginTop: "12px",
    fontSize: "13px",
    fontWeight: 500,
    color: GREEN,
    textDecoration: "underline",
    cursor: "pointer",
  },
};

/**
 * Running a task counts as doing it (the click is the signal — we can't see
 * inside WhatsApp). It stays clickable afterwards so it can be done again.
 */
export const useRunTask = ({ onNavigate } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { t } = useTranslation("page-grow-community");
  const { markDone } = useWeeklyTasks();

  return useCallback(
    (task) => {
      markDone(task.id);

      if (task.action === "copyJoinLink") {
        copyText(MOCK_JOIN_LINK).then((copied) =>
          snackbar(copied ? t("tasks.join_link_copied") : t("copy_failed"), copied ? "success" : "error"),
        );
        return;
      }
      const target = SCROLL_TARGETS[task.action];
      if (target) {
        if (location.pathname === GROW_COMMUNITY_PATH) {
          revealSection(target);
        } else {
          onNavigate?.();
          navigate(`${GROW_COMMUNITY_PATH}#${target}`);
        }
        return;
      }
      if (task.route) {
        onNavigate?.();
        navigate(task.route);
      }
    },
    [markDone, snackbar, t, location.pathname, navigate, onNavigate],
  );
};

const TaskRow = ({ task, compact, lang, t, isRTL, onRun }) => {
  const vars = resolveVars(task.vars, lang);
  const Chevron = isRTL ? ChevronLeftIcon : ChevronRightIcon;
  const leadsSomewhere = !!(task.route || task.action);

  return (
    <ButtonBase onClick={() => onRun(task)} sx={compact ? s.compactRow : s.row}>
      {task.done ? (
        <CheckCircleIcon sx={{ color: GREEN, fontSize: compact ? 18 : 20 }} aria-hidden="true" />
      ) : (
        <RadioButtonUncheckedIcon sx={{ color: "rgba(62, 63, 76, 0.35)", fontSize: compact ? 18 : 20 }} aria-hidden="true" />
      )}
      <Box sx={s.rowText}>
        <Typography sx={{ ...s.label(task.done), ...(compact ? { fontSize: "13px", fontWeight: 400 } : {}) }}>
          {t(`tasks.types.${task.type}.label`, vars)}
        </Typography>
        {!compact && <Typography sx={s.hint}>{t(`tasks.types.${task.type}.hint`, vars)}</Typography>}
      </Box>
      {!compact && leadsSomewhere && <Chevron sx={s.chevron} aria-hidden="true" />}
    </ButtonBase>
  );
};

/** Full card on the Grow your community page (an accordion on mobile). */
const WeeklyTasks = ({ collapsible = false, expanded = true, onToggle, onEntered }) => {
  const { t, i18n } = useTranslation("page-grow-community");
  const lang = (i18n.language || "he").startsWith("he") ? "he" : "en";
  const { tasks, completed, total, percentage } = useWeeklyTasks();
  const run = useRunTask();

  return (
    <SectionCard
      title={t("tasks.title")}
      subtitle={t("tasks.subtitle")}
      collapsible={collapsible}
      expanded={expanded}
      onToggle={onToggle}
      onEntered={onEntered}
      isRTL={lang === "he"}
      bodyId="weekly-tasks-body"
    >
      <Box sx={{ ...g.controlRow, ...s.progressRow }}>
        <Box sx={s.progressBar}>
          <LinearProgressBar percentage={percentage} showPercentageText={false} />
        </Box>
        <Typography sx={s.progressCount}>{t("tasks.progress", { done: completed, total })}</Typography>
      </Box>
      {total > 0 && completed === total && <Typography sx={s.allDone}>{t("tasks.all_done")}</Typography>}
      <Box sx={s.list}>
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} lang={lang} t={t} isRTL={lang === "he"} onRun={run} />
        ))}
      </Box>
    </SectionCard>
  );
};

/** Compact list for the sidebar Get Started popup, once setup is complete. */
export const WeeklyTasksCompact = ({ onNavigate }) => {
  const { t, i18n } = useTranslation("page-grow-community");
  const lang = (i18n.language || "he").startsWith("he") ? "he" : "en";
  const navigate = useNavigate();
  const { tasks } = useWeeklyTasks();
  const run = useRunTask({ onNavigate });

  return (
    <Box>
      {tasks.map((task) => (
        <TaskRow key={task.id} task={task} compact lang={lang} t={t} isRTL={lang === "he"} onRun={run} />
      ))}
      <Typography
        component="button"
        type="button"
        onClick={() => {
          onNavigate?.();
          navigate(GROW_COMMUNITY_PATH);
        }}
        sx={{ ...s.openPage, background: "none", border: "none", padding: 0, fontFamily: "inherit" }}
      >
        {t("tasks.open_page")}
      </Typography>
    </Box>
  );
};

export default WeeklyTasks;
